import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendInvite } from '@/lib/api/invite';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { useDefaultInviteLocale } from '@/components/InviteLocaleSelect';
import type { Player } from '@swiss-magic/shared-types';

interface UseInviteDialogParams {
  tournamentId: string | undefined;
}

export function useInviteDialog({ tournamentId }: UseInviteDialogParams) {
  const { t } = useTranslation('director');
  const defaultPickerLocale = useDefaultInviteLocale();
  const [inviteTarget, setInviteTarget] = useState<Player | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLocale, setInviteLocale] = useState<string>(defaultPickerLocale);
  const [sendingInvite, setSendingInvite] = useState(false);

  function openInviteDialog(player: Player) {
    setInviteTarget(player);
    setInviteEmail('');
    setInviteLocale(defaultPickerLocale);
  }

  function closeInviteDialog() {
    setInviteTarget(null);
    setInviteEmail('');
    setInviteLocale(defaultPickerLocale);
    setSendingInvite(false);
  }

  async function handleSendInvite() {
    if (!inviteTarget || !tournamentId) return;
    setSendingInvite(true);
    try {
      await sendInvite(
        tournamentId,
        inviteTarget.id,
        inviteEmail.trim(),
        inviteLocale,
      );
      showSuccessToast(
        t('players_page.invite_dialog.sent_toast', { email: inviteEmail.trim() }),
      );
      closeInviteDialog();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('players_page.invite_dialog.default_error');
      showErrorToast(message);
    } finally {
      setSendingInvite(false);
    }
  }

  return {
    inviteTarget,
    openInviteDialog,
    closeInviteDialog,
    inviteEmail,
    setInviteEmail,
    inviteLocale,
    setInviteLocale,
    sendingInvite,
    handleSendInvite,
  };
}
